import React, { useCallback, useMemo, useEffect } from 'react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import QuestionCircleIcon from '@atlaskit/icon/core/migration/question-circle';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss, Inline, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useFlagsService } from '@atlassian/jira-flags';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { EChartsEvent, BatchSizeChartDataPoint } from '../../common/types.tsx';
import { TrendLozenge } from '../../common/ui/trend-lozenge/index.tsx';
import { IssuesTrendSummary } from '../../common/ui/trend-summary/index.tsx';
import { getWeekRangeFromWeekStartDate } from '../../common/utils/date-range.tsx';
import { useSelectedWeekStore } from '../../controllers/selected-date-range/index.tsx';
import { useSelectedDeploymentActions } from '../../controllers/selected-deployment/index.tsx';
import { useReportCoreData } from '../../services/deployment-frequency-core/index.tsx';
import { useViewDeploymentFrequencyReportExperienceFail } from '../../services/experience-tracker/index.tsx';
import { usePeriodBreakdownMetrics } from '../../services/period-breakdown/index.tsx';
import Chart from './chart/index.tsx';
import { convertToDataPoints } from './chart/utils.tsx';
import { LoadingSkeleton } from './loading-skeleton/index.tsx';
import messages from './messages.tsx';

const ChartTitle = () => {
	const { formatMessage } = useIntl();

	return (
		<Heading size="xsmall" as="h3">
			<Inline xcss={WeekBreakDownTitleComponent}>
				{formatMessage(messages.weekBreakDownTitle)}
				<Tooltip content={formatMessage(messages.weekBreakDownTitleTooltip)} position="right">
					<Box xcss={questionIconStyles}>
						<QuestionCircleIcon LEGACY_size="small" label="" />
					</Box>
				</Tooltip>
			</Inline>
		</Heading>
	);
};

export const BatchSizeChart = () => {
	const onViewDeploymentFrequencyReportExperienceFail =
		useViewDeploymentFrequencyReportExperienceFail();
	const { formatDate, formatMessage, formatNumber } = useIntl();
	const [{ selectedWeekStart }] = useSelectedWeekStore();
	const selectedDateRange = selectedWeekStart
		? getWeekRangeFromWeekStartDate(selectedWeekStart)
		: null;
	const { data: coreData, error: coreError, fetch: coreFetch } = useReportCoreData();
	const { loading, error, data, fetch } = usePeriodBreakdownMetrics(selectedDateRange);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setSelectedDeploymentId }] = useSelectedDeploymentActions();
	const { showFlag } = useFlagsService();
	const onChartClicked = useCallback(
		({ data: eventData }: EChartsEvent<BatchSizeChartDataPoint>) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'batchSizeChartDeployment',
				}),
			);
			setSelectedDeploymentId(eventData.name);
		},
		[createAnalyticsEvent, setSelectedDeploymentId],
	);
	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);

	useEffect(() => {
		if (error && data == null) {
			onViewDeploymentFrequencyReportExperienceFail('BatchSizeChart', error);
			fireErrorAnalytics({
				meta: {
					id: 'BatchSizeChartDataFailed',
					packageName: 'jiraSoftwareDeploymentFrequencyReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
		if (error && data !== null) {
			// Non-fatal error - log it and show a flag
			fireErrorAnalytics({
				meta: {
					id: 'DeploymentMissingInPerDeploymentMetrics',
					packageName: 'jiraSoftwareDeploymentFrequencyReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			showFlag({
				type: 'error',
				title: formatMessage(messages.errorFlagTitle),
				description: formatMessage(messages.errorFlagDescription),
				messageId: 'software-deployment-frequency-report.ui.batch-size-chart.show-flag.error',
				messageType: 'transactional',
			});
		}
	}, [data, error, onViewDeploymentFrequencyReportExperienceFail, formatMessage, showFlag]);

	if (error && data == null) {
		// if core data is failed, it doesn't make sense to retry on perDeploymentMetrics
		return (
			<>
				<ChartTitle />
				<Inline
					xcss={errorUIWrapperStyles}
					testId="software-deployment-frequency-report.ui.batch-size-chart.error-ui"
				>
					<ErrorRetry onRetry={coreError && coreData == null ? coreFetch : fetch} />
				</Inline>
			</>
		);
	}

	if (loading || data == null || selectedWeekStart == null || selectedDateRange === null) {
		return <LoadingSkeleton />;
	}

	const isEmptyState = data.perDeploymentMetrics.length === 0;
	const dataPoints = convertToDataPoints(data.perDeploymentMetrics);

	return (
		<Box
			xcss={chartWrapperBaseStyles}
			// eslint-disable-next-line jira/integration/enforce-data-testid-usage
			data-test-id="software-deployment-frequency-report.ui.batch-size-chart"
		>
			<ChartTitle />
			<Heading size="xxsmall" as="h3">
				<Box xcss={periodDateComponentStyles}>
					{formatMessage(messages.weekTitle, {
						date: formatDate(selectedWeekStart, {
							month: 'short',
							day: 'numeric',
						}),
					})}
				</Box>
			</Heading>

			<Inline xcss={titleContainerStyles}>
				<Heading size="medium" as="h3">
					<Box xcss={titleComponentStyles}>
						{fg('jira-issue-terminology-refresh-m3')
							? formatMessage(messages.issuesPerDeploymentIssueTermRefresh, {
									periodProdIssuesPerDeployment: formatNumber(
										data.periodProdIssuesPerDeployment ?? 0,
									),
								})
							: formatMessage(messages.issuesPerDeployment, {
									periodProdIssuesPerDeployment: formatNumber(
										data.periodProdIssuesPerDeployment ?? 0,
									),
								})}
					</Box>
				</Heading>
				{data.periodProdDeployments > 0 && (
					<Inline xcss={trendSummaryContainerStyles}>
						{data.prodDataState === 'SUFFICIENT_FOR_TREND' && (
							<Box as="span" xcss={trendLozengeContainerStyles}>
								<TrendLozenge
									current={data.periodProdIssuesPerDeployment ?? 0}
									median={data.medianIssuesPerDeployment ?? 0}
									colorScheme="UP_IS_BAD"
								/>
							</Box>
						)}
						<IssuesTrendSummary
							weekCount={data.periodProdIssuesPerDeployment ?? 0}
							wholePeriodStatistic={data.medianIssuesPerDeployment ?? 0}
							prodDataState={data.prodDataState}
						/>
					</Inline>
				)}
			</Inline>
			<Inline xcss={descriptionAndLegendContainerStyles}>
				{isEmptyState ? (
					<Lozenge appearance="new">{formatMessage(messages.moreDataRequired)}</Lozenge>
				) : (
					<>
						<Inline as="span" xcss={descriptionStyles}>
							<IssuesIcon LEGACY_size="small" label="" />
							<Text as="span" size="small">
								{formatMessage(messages.weeklyProdDeployments, {
									weeklyProdDeployments: data.periodProdDeployments,
								})}
							</Text>
						</Inline>
						<Inline xcss={legendContainerStyles}>
							<Inline xcss={legendEntityStyles}>
								<Box xcss={chartSymbolStyles} />
								<Text as="span" size="small">
									{formatMessage(messages.deployment)}
								</Text>
							</Inline>
							<Inline xcss={legendEntityStyles}>
								<Text size="small">--</Text>
								<Text as="span" size="small">
									{formatMessage(messages.twelveWeekMedian)}
								</Text>
							</Inline>
						</Inline>
					</>
				)}
			</Inline>
			<Box xcss={graphBaseStyles}>
				<Chart
					dataPoints={dataPoints}
					median={data.medianIssuesPerDeployment}
					dateRange={selectedDateRange}
					onEvents={events}
				/>
			</Box>
		</Box>
	);
};

const titleContainerStyles = xcss({
	marginTop: 'space.050',
	alignItems: 'center',
});

const descriptionStyles = xcss({
	color: 'color.text.subtle',
	alignItems: 'center',
	gap: 'space.050',
});

const descriptionAndLegendContainerStyles = xcss({
	justifyContent: 'space-between',
	alignItems: 'center',
	marginTop: 'space.100',
});

const legendContainerStyles = xcss({
	alignItems: 'center',
	paddingRight: 'space.250',
});

const legendEntityStyles = xcss({
	alignItems: 'center',
	paddingLeft: 'space.100',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '8px',
	gap: 'space.050',
});

const trendSummaryContainerStyles = xcss({
	marginLeft: 'space.150',
	alignItems: 'center',
});

const errorUIWrapperStyles = xcss({
	height: '272px',
	justifyContent: 'center',
	alignItems: 'center',
});

const questionIconStyles = xcss({
	marginLeft: 'space.050',
	fill: 'color.icon.inverse',
	color: 'color.text',
	cursor: 'pointer',
	paddingTop: 'space.025',
});

const trendLozengeContainerStyles = xcss({
	marginRight: 'space.050',
});

const chartSymbolStyles = xcss({
	width: token('space.100'),
	height: token('space.100'),
	borderRadius: '100%',
	backgroundColor: 'color.background.accent.blue.subtle',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderColor: 'color.border.accent.blue',
});

const titleComponentStyles = xcss({
	color: 'color.text',
});

const periodDateComponentStyles = xcss({
	margin: 'space.0',
	color: 'color.text',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',
});

const WeekBreakDownTitleComponent = xcss({
	height: '24px',
	alignItems: 'center',
	marginBottom: 'space.200',
});

const graphBaseStyles = xcss({
	height: '272px',
});

const chartWrapperBaseStyles = xcss({
	padding: 'space.0',
});
